// Sem imports de runtime: testável com `node --test` puro (ver normalizar.ts
// da esteira). `import type` some na transpilação.
import type { CobrancaCliente, CobrancaStatusNegociacao } from '../../types';

/**
 * Como ficou o cliente depois de um contato de cobrança (#6165).
 *
 * Pedido da Meyryene (05/10/2026): todo contato registrado sai com o próximo
 * passo — data de retorno ou status. Antes dava para registrar sem nada: o
 * Daniel Carvalho teve contato em 29/09 e o card continuou "retorno vencido há
 * 67 dias", pela data de 30/07 que ninguém trocou.
 *
 *   • retorno    — segue em cobrança; volta ao painel na data;
 *   • negociando — está conversando; volta na data para saber como ficou;
 *   • negociado  — fechou acordo; volta no dia seguinte ao pagamento combinado
 *                  (se pagou, não deve mais nada e não aparece);
 *   • pago       — pagou; sai do painel e a régua dele fecha.
 *
 * Negativar continua no "Gerenciar negociação": lá se escolhe quem negativa.
 */
export type DesfechoContato = 'retorno' | 'negociando' | 'negociado' | 'pago';

export interface CamposDesfecho {
    desfecho: DesfechoContato;
    /** 'YYYY-MM-DD' — retorno e negociando. */
    proximoContato: string;
    /** Valor combinado (negociado) ou recebido (pago), como veio do input. */
    valor: string;
    /** 'YYYY-MM-DD' — pagamento combinado (negociado) ou recebido (pago). */
    dataPagamento: string;
}

export const DESFECHOS: Array<{ valor: DesfechoContato; rotulo: string }> = [
    { valor: 'retorno', rotulo: 'Retornar depois' },
    { valor: 'negociando', rotulo: 'Negociando' },
    { valor: 'negociado', rotulo: 'Fechou acordo' },
    { valor: 'pago', rotulo: 'Pagou' },
];

/** Começo do formulário: retorno, com a data que já estava marcada se for futura. */
export function desfechoInicial(proximoAtual: string | null | undefined, hoje: string): CamposDesfecho {
    return {
        desfecho: 'retorno',
        proximoContato: proximoAtual && proximoAtual > hoje ? proximoAtual : '',
        valor: '',
        dataPagamento: '',
    };
}

/** 'YYYY-MM-DD' do dia seguinte, sem passar por UTC. */
export function diaSeguinte(iso: string): string {
    const d = new Date(`${iso}T12:00:00`);
    d.setDate(d.getDate() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** O que falta para registrar; `null` quando está tudo certo. `hoje` em 'YYYY-MM-DD'. */
export function faltaNoDesfecho(c: CamposDesfecho, hoje: string): string | null {
    switch (c.desfecho) {
        case 'retorno':
        case 'negociando':
            if (!c.proximoContato) return 'Marque a data do próximo contato.';
            if (c.proximoContato <= hoje) return 'O próximo contato precisa ser a partir de amanhã.';
            return null;
        case 'negociado':
            if (!(Number(c.valor) > 0)) return 'Informe o valor combinado.';
            if (!c.dataPagamento) return 'Informe até quando o cliente vai pagar.';
            if (c.dataPagamento < hoje) return 'A data combinada não pode ser no passado.';
            return null;
        case 'pago':
            if (!c.dataPagamento) return 'Informe quando o pagamento entrou.';
            if (c.dataPagamento > hoje) return 'A data do pagamento não pode ser no futuro.';
            if (c.valor && !(Number(c.valor) > 0)) return 'O valor recebido precisa ser maior que zero.';
            return null;
    }
}

/** Quando o cliente volta ao painel. `null` = não volta (pagou). */
export function retornoDoDesfecho(c: CamposDesfecho): string | null {
    if (c.desfecho === 'pago') return null;
    if (c.desfecho === 'negociado') return diaSeguinte(c.dataPagamento);
    return c.proximoContato || null;
}

/** Status de negociação que o desfecho grava; `null` mantém o atual. */
export function statusDoDesfecho(d: DesfechoContato): CobrancaStatusNegociacao | null {
    return d === 'retorno' ? null : d;
}

export type CamposStatusDesfecho = Partial<Pick<CobrancaCliente,
    | 'valor_negociado' | 'data_pagamento_negociado' | 'proximo_contato_em'
    | 'pago_em' | 'pago_por_id' | 'pago_por_nome' | 'valor_pago'
>>;

/**
 * Colunas que acompanham o status — as mesmas dos botões "Marcar como
 * Negociado" e "Marcar como Pago" do Gerenciar Cobrança.
 */
export function camposDoDesfecho(
    c: CamposDesfecho,
    quem: { id: string | null; nome: string },
): CamposStatusDesfecho {
    if (c.desfecho === 'negociado') {
        return {
            valor_negociado: Number(c.valor),
            data_pagamento_negociado: c.dataPagamento,
            proximo_contato_em: diaSeguinte(c.dataPagamento),
        };
    }
    if (c.desfecho === 'pago') {
        return {
            // Meio-dia de Brasília: 'yyyy-mm-dd' lido como UTC voltaria um dia.
            pago_em: new Date(`${c.dataPagamento}T12:00:00-03:00`).toISOString(),
            pago_por_id: quem.id,
            pago_por_nome: quem.nome,
            valor_pago: c.valor ? Number(c.valor) : null,
        };
    }
    return {};
}

/** O que o cliente vai ver acontecer — vira a ajuda do formulário e o aviso de sucesso. */
export function avisoDoDesfecho(c: CamposDesfecho): string | null {
    const dia = (iso: string) => iso.split('-').reverse().join('/');
    if (c.desfecho === 'pago') return 'O cliente sai do painel e a régua de cobrança dele é encerrada.';
    const volta = c.desfecho === 'negociado'
        ? (c.dataPagamento ? diaSeguinte(c.dataPagamento) : '')
        : c.proximoContato;
    if (!volta) return null;
    return c.desfecho === 'negociado'
        ? `O cliente sai do painel. Se não pagar, volta em ${dia(volta)}, um dia depois do combinado.`
        : `O cliente sai do painel de todo mundo e volta em ${dia(volta)}.`;
}
